import { Button } from './ui/Button';
import type { PlanningSessionStatus, JiraTicketRef } from '@pantheon/shared';

export interface ApprovalBarProps {
  status: PlanningSessionStatus;
  ticketCount: number;
  jiraResults?: { createdTickets: JiraTicketRef[]; errors?: string[] };
  onApprove: () => void;
  onRegenerate: () => void;
  onReset: () => void;
  loading: boolean;
  className?: string;
}

export function ApprovalBar({
  status, ticketCount, jiraResults, onApprove, onRegenerate, onReset, loading, className = '',
}: ApprovalBarProps) {
  const isReviewing = status === 'reviewing';
  const isComplete = status === 'completed';
  const isFailed = status === 'failed';

  const createdCount = jiraResults?.createdTickets.filter((t) => t.status === 'created').length ?? 0;

  return (
    <div className={`flex items-center justify-between px-4 py-3 bg-white rounded-2xl border border-slate-100 shadow-sm shadow-slate-200/50 mt-4 ${className}`}>
      {/* Status */}
      <div className="text-sm">
        {status === 'creating_tickets' && <span className="text-gray-500">Creating tickets in Jira...</span>}
        {isComplete && <span className="text-success font-medium">{createdCount} tickets created in Jira</span>}
        {isFailed && jiraResults?.errors && <span className="text-danger font-medium">{jiraResults.errors[0]}</span>}
        {isReviewing && <span className="text-gray-500">{ticketCount} tickets ready for review</span>}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2">
        {isReviewing && (
          <>
            <Button variant="secondary" size="sm" onClick={onRegenerate} loading={loading}>Regenerate</Button>
            <Button size="sm" onClick={onApprove} loading={loading}>Approve &amp; Create in Jira</Button>
          </>
        )}

        {isComplete && (
          <div className="flex items-center gap-3">
            <div className="flex flex-wrap gap-1.5">
              {jiraResults?.createdTickets
                .filter((t) => t.status === 'created')
                .slice(0, 5)
                .map((t) => (
                  <a key={t.jiraKey} href={t.jiraUrl} target="_blank" rel="noopener noreferrer"
                    className="text-xs text-primary hover:underline bg-primary-light px-2.5 py-0.5 rounded-full">
                    {t.jiraKey}
                  </a>
                ))}
              {createdCount > 5 && <span className="text-xs text-gray-500">+{createdCount - 5} more</span>}
            </div>
            <Button variant="secondary" size="sm" onClick={onReset}>New Session</Button>
          </div>
        )}

        {isFailed && (
          <>
            {ticketCount > 0 ? (
              <Button variant="secondary" size="sm" onClick={onApprove} loading={loading}>Retry Jira</Button>
            ) : (
              <Button variant="secondary" size="sm" onClick={onRegenerate} loading={loading}>Regenerate</Button>
            )}
            <Button variant="ghost" size="sm" onClick={onReset}>Start Over</Button>
          </>
        )}
      </div>
    </div>
  );
}
